import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Divider, Chip, Button, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../constants';
import { EXAMPLE_SOURCES } from '../constants/exampleSources';

// ── Typografi-hjälpare ────────────────────────────────────────────────────────
const codeBg = {
  bgcolor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 1.5,
  p: 2,
  fontFamily: 'monospace',
  fontSize: '0.82rem',
  color: '#a8d8a8',
  overflowX: 'auto',
  whiteSpace: 'pre',
};

function H2({ id, children }) {
  return (
    <Typography id={id} variant="h6" fontWeight={800}
      sx={{ color: 'white', mb: 1.5, mt: 5, scrollMarginTop: 24 }}>
      {children}
    </Typography>
  );
}
function H3({ children }) {
  return (
    <Typography fontWeight={700}
      sx={{ color: '#ccc', mb: 1, mt: 2.5, fontSize: '0.9rem' }}>
      {children}
    </Typography>
  );
}
function P({ children }) {
  return (
    <Typography sx={{ color: '#999', fontSize: '0.88rem', lineHeight: 1.8, mb: 1.5 }}>
      {children}
    </Typography>
  );
}
function Inline({ children }) {
  return (
    <Box component="code"
      sx={{ bgcolor: 'rgba(0,137,123,0.12)', color: APPLAB_ACCENT, px: 0.7, py: 0.2, borderRadius: 0.5, fontSize: '0.82em', fontFamily: 'monospace' }}>
      {children}
    </Box>
  );
}

// ── Block-katalog ─────────────────────────────────────────────────────────────
const BLOCKS = [
  {
    name: 'ai.chat',
    status: 'available',
    description: 'Anropa Claude med konversationsformat. Stöder multi-turn historik, system-prompts och valfri modell.',
    schema: `{
  messages:   Array<{ role: "user" | "assistant", content: string }>,  // krävs
  model:      string,   // valfritt – default "claude-sonnet-4-6"
  max_tokens: number,   // valfritt – default 1024
  system:     string,   // valfritt – system-prompt
}`,
    returns: `{
  content:  string,   // Claude:s svar som ren text
  model:    string,   // modellen som användes
  usage: { tokens_in: number, tokens_out: number, cost_usd: number }
}`,
    example: `const res = await AppLabSDK.run('ai.chat', {
  messages: [{ role: 'user', content: 'Förklara betong kort.' }],
  model: 'claude-sonnet-4-6',
  max_tokens: 512,
  system: 'Svara alltid på svenska.',
});
console.log(res.content); // "Betong är ett byggmaterial som..."`,
  },
  {
    name: 'ai.extract',
    status: 'available',
    description: 'Extrahera strukturerad data ur fritext med Claude. Du definierar ett schema (objekt med fältbeskrivningar) och får tillbaka ett JSON-objekt. Använder Haiku som default för låg kostnad.',
    schema: `{
  text:       string,   // krävs – texten att extrahera från
  schema:     object,   // krävs – { fältnamn: "beskrivning", ... }
  model:      string,   // valfritt – default "claude-haiku-4-5"
  max_tokens: number,   // valfritt – default 1024
}`,
    returns: `{
  result: object,   // extraherat JSON-objekt enligt ditt schema
  model:  string,
  usage:  { tokens_in: number, tokens_out: number, cost_usd: number }
}`,
    example: `const res = await AppLabSDK.run('ai.extract', {
  text: 'Offert från Skanska 2024-03-15, totalt 450 000 kr exkl moms.',
  schema: {
    leverantor: 'Leverantörens namn',
    datum:      'Datum (YYYY-MM-DD)',
    belopp:     'Belopp i SEK (bara siffra)',
  },
});
console.log(res.result); // { leverantor: "Skanska", datum: "2024-03-15", belopp: 450000 }`,
  },
  {
    name: 'qr.generate',
    status: 'available',
    description: 'Generera en QR-kod som PNG (base64 data URL). Inga AI-tokens förbrukas — ingår gratis i AppLAB.',
    schema: `{
  text:             string,   // krävs – text eller URL att koda
  size:             number,   // valfritt – px, 100–1000 (default 300)
  error_correction: string,   // valfritt – "L"|"M"|"Q"|"H" (default "M")
}`,
    returns: `{
  data_url: string,   // "data:image/png;base64,..." — sätt som src på <img>
  width:    number,
  height:   number,
}`,
    example: `const res = await AppLabSDK.run('qr.generate', {
  text: 'https://lagbasn.se/p/mitt-projekt',
  size: 400,
  error_correction: 'H',
});
document.getElementById('qr').src = res.data_url;`,
  },
  {
    name: 'chart.render',
    status: 'available',
    description: 'Rendera ett diagram som SVG-sträng (bar, line, pie). Returnerar SVG som kan injiceras direkt i DOM:en eller visas som bild. Inga AI-tokens förbrukas.',
    schema: `{
  type:     string,           // "bar" | "line" | "pie" (default "bar")
  labels:   string[],         // krävs – x-axelns etiketter (eller pie-segment-namn)
  datasets: Array<{
    label:  string,           // namn på dataserien (visas i legend)
    data:   number[],         // datapunkter, en per label
    color:  string,           // valfritt CSS-färg
    colors: string[],         // valfritt – per-segment-färg (pie)
  }>,
  title:    string,           // valfritt – diagramtitel
  width:    number,           // valfritt – px (default 600)
  height:   number,           // valfritt – px (default 380)
}`,
    returns: `{
  svg:    string,   // komplett SVG-sträng
  type:   string,
  width:  number,
  height: number,
}`,
    example: `const res = await AppLabSDK.run('chart.render', {
  type: 'bar',
  labels: ['Jan', 'Feb', 'Mar', 'Apr'],
  datasets: [
    { label: 'Intäkter', data: [120, 145, 98, 172], color: '#00897B' },
    { label: 'Kostnader', data: [80, 95, 110, 90], color: '#FF6B35' },
  ],
  title: 'Kvartal 1',
});
// Injicera SVG direkt i DOM
document.getElementById('chart').innerHTML = res.svg;`,
  },
  {
    name: 'wordcloud.generate',
    status: 'available',
    description: 'Räkna ordfrekvens i text. Filtrerar bort svenska och engelska stoppord. Returnerar en sorterad lista med ord och frekvenser — rendera själv som ordmoln, stapel, tabell eller annat. Inga AI-tokens förbrukas.',
    schema: `{
  text:       string,   // krävs – texten att analysera
  max_words:  number,   // valfritt – max antal ord i resultat (default 50, max 200)
  min_length: number,   // valfritt – minsta ordlängd (default 3)
}`,
    returns: `{
  words: Array<{ text: string, value: number }>,  // sorterat efter frekvens
  total_unique: number,                            // antal unika ord (före max_words-trunkering)
}`,
    example: `const res = await AppLabSDK.run('wordcloud.generate', {
  text: feedbackText,
  max_words: 30,
});
res.words.forEach(({ text, value }) => {
  const el = document.createElement('span');
  el.style.fontSize = (0.8 + (value / res.words[0].value) * 1.2) + 'rem';
  el.textContent = text;
  container.appendChild(el);
});`,
  },
];

// ── Exempelappar ──────────────────────────────────────────────────────────────
const EXAMPLES = [
  { file: 'chat.html',              label: 'AI-chatt',            desc: 'Multi-turn chatt med konversationshistorik. Bra startpunkt för de flesta AI-appar.' },
  { file: 'summarizer.html',        label: 'Textsammanfattare',   desc: 'Klistra in text och välj sammanfattningslängd. Visar token-användning.' },
  { file: 'model-comparison.html',  label: 'Modell-jämförelse',   desc: 'Kör samma prompt mot Haiku, Sonnet och Opus sida vid sida. Pedagogiskt för att förstå skillnaderna.' },
  { file: 'qr-generator.html',      label: 'QR-generator',        desc: 'Generera QR-koder för URL:er eller fritext. Ladda ner som PNG eller kopiera till urklipp. Använder qr.generate.' },
  { file: 'document-analyzer.html', label: 'Dokumentanalys',      desc: 'Klistra in ett mejl, offert eller protokoll och definiera vad som ska extraheras. Använder ai.extract.' },
  { file: 'feedback-analysis.html', label: 'Feedbackanalys',      desc: 'Analysera ordfrekvens i fritext. Visar topp-10 som stapeldiagram och alla ord som ordmoln. Använder wordcloud.generate + chart.render.' },
];

function downloadExample(fileName) {
  const content = EXAMPLE_SOURCES[fileName];
  if (!content) return;
  const blob = new Blob([content], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Modell-info ───────────────────────────────────────────────────────────────
const MODELS = [
  {
    id: 'claude-haiku-4-5', label: 'Haiku 4.5',
    badge: 'Billigast', badgeColor: '#607D8B',
    tip: 'Snabb och billig. Bra för klassificering, korta svar, enkel extraktion.',
    costIn: 0.80, costOut: 4.00,
  },
  {
    id: 'claude-sonnet-4-6', label: 'Sonnet 4.6',
    badge: 'Rekommenderat', badgeColor: APPLAB_ACCENT,
    tip: 'Standardval. Bra för det mesta – sammanfattningar, analys, chattbotar.',
    costIn: 3.00, costOut: 15.00,
  },
  {
    id: 'claude-opus-4-7', label: 'Opus 4.7',
    badge: 'Kraftfullast', badgeColor: '#FF7043',
    tip: 'Bäst kvalitet men dyrast. Använd bara när resultatets kvalitet är kritisk.',
    costIn: 5.00, costOut: 25.00,
    warning: 'Opus 4.7 använder en ny tokenizer som kan ge ~35% fler tokens för samma text jämfört med Sonnet. Den faktiska kostnaden är ofta högre än priset antyder.',
  },
];

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: 'Min app funkar inte när jag dubbelklickar på index.html.',
    a: 'Det är by design. SDK:t kommunicerar via postMessage till LagbasN, och den kanalen finns bara när appen körs i en iframe på lagbasn.se. Ladda upp filen via Ladda upp app och öppna den därifrån.',
  },
  {
    q: 'Varför varierar kostnaderna så mycket?',
    a: 'AI-modeller faktureras per token – en token är ungefär 3–4 tecken. Långa texter, komplexa frågor och långa svar ger fler tokens. Opus 4.7 har dessutom en ny tokenizer som genererar ~35% fler tokens för samma text.',
  },
  {
    q: 'Kan jag lagra data mellan sessioner?',
    a: 'Ja, localStorage fungerar inom sandbox-domänen och kvarstår mellan besök. Tänk på att det inte är delat mellan användare – det är per webbläsare och per app.',
  },
  {
    q: 'Kan min app anropa externa API:er?',
    a: 'Inte direkt. Sandbox-domänens CSP tillåter bara anslutningar till lagbasn.se och applab.lagbasn.se. Externt API-anrop kan bli ett framtida block i AppLAB.',
  },
  {
    q: 'Räknas varje meddelande i en chatt mot kvoten?',
    a: 'Ja, varje AppLabSDK.run()-anrop räknas separat. I en chattapp med historik ökar kostnaden för varje tur eftersom hela konversationen skickas som context. Håll historiken rimlig.',
  },
  {
    q: 'Vad händer om jag bygger något fantastiskt?',
    a: 'Hör av dig till Johan – appen kan lyftas in i LagbasN som en fullständig modul.',
  },
];

// ── Innehållsförteckning ──────────────────────────────────────────────────────
const TOC = [
  { id: 'kom-igang',       label: '1. Kom igång' },
  { id: 'api',             label: '2. API-referens' },
  { id: 'block-katalog',   label: '3. Block-katalog' },
  { id: 'modellguide',     label: '4. Modellguide' },
  { id: 'kvoter',          label: '5. Kvoter & kostnader' },
  { id: 'exempelappar',    label: '6. Exempelappar' },
  { id: 'faq',             label: '7. Vanliga frågor' },
  { id: 'feedback',        label: '8. Saknar du något?' },
];

// ── Sidkomponent ──────────────────────────────────────────────────────────────
export default function DocsPage() {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(null);

  function handleDownload(file) {
    setDownloading(file);
    downloadExample(file);
    setDownloading(null);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2, position: 'sticky', top: 0, zIndex: 100 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box onClick={() => navigate('/applab')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
          </Box>
          <Typography sx={{ color: '#444' }}>›</Typography>
          <Typography fontWeight={700} fontSize="0.9rem">Dokumentation</Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h4" fontWeight={900} sx={{ mb: 0.5 }}>
          App<span style={{ color: APPLAB_ORANGE }}>LAB</span> SDK
        </Typography>
        <Typography sx={{ color: '#666', mb: 3, fontSize: '0.9rem' }}>
          Bygg HTML-appar som anropar Claude via ett enkelt JavaScript-API.
        </Typography>

        {/* Innehållsförteckning */}
        <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 2.5, mb: 4 }}>
          <Typography fontSize="0.75rem" fontWeight={700} sx={{ color: '#555', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
            Innehåll
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {TOC.map(t => (
              <Box key={t.id} component="a" href={`#${t.id}`}
                sx={{ fontSize: '0.82rem', color: '#888', textDecoration: 'none', '&:hover': { color: APPLAB_ACCENT } }}>
                {t.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 1 }} />

        {/* ── 1. Kom igång ─────────────────────────────────────────────────── */}
        <H2 id="kom-igang">1. Kom igång</H2>
        <P>Skapa en vanlig HTML-fil och inkludera SDK:t i <Inline>{'<head>'}</Inline>:</P>
        <Box sx={codeBg}>{`<script src="/applab-sdk.js"></script>`}</Box>

        <P sx={{ mt: 2 }}>SDK:t exponerar det globala objektet <Inline>AppLabSDK</Inline>. Första anropet:</P>
        <Box sx={codeBg}>{`<!DOCTYPE html>
<html lang="sv">
<head>
  <meta charset="UTF-8">
  <script src="/applab-sdk.js"></script>
</head>
<body>
  <button onclick="fråga()">Fråga Claude</button>
  <p id="svar"></p>
  <script>
    async function fråga() {
      const res = await AppLabSDK.run('ai.chat', {
        messages: [{ role: 'user', content: 'Hej! Vad är AppLAB?' }],
      });
      document.getElementById('svar').textContent = res.content;
    }
  </script>
</body>
</html>`}</Box>

        <P>Ladda upp filen via <Box component="span" onClick={() => navigate('/applab/upload')}
          sx={{ color: APPLAB_ACCENT, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          Ladda upp app
        </Box>. Appen fungerar bara när den körs inuti LagbasN – SDK:t visar en varningsbanner om du öppnar HTML-filen direkt i webbläsaren.</P>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 2. API-referens ───────────────────────────────────────────────── */}
        <H2 id="api">2. API-referens</H2>

        <H3>AppLabSDK.run(block, params)</H3>
        <P>Anropar ett AppLAB-block. Räknas mot användarens månadskvot.</P>
        <Box sx={codeBg}>{`const res = await AppLabSDK.run('ai.chat', { messages, model, max_tokens, system });
// → { content: string, model: string, usage: { tokens_in, tokens_out, cost_usd } }`}</Box>

        <H3>AppLabSDK.getCurrentUser()</H3>
        <P>Hämtar den inloggade användaren. Räknas inte mot kvoten.</P>
        <Box sx={codeBg}>{`const user = await AppLabSDK.getCurrentUser();
// → { displayName: string, email: string, photoURL: string | null }`}</Box>

        <H3>AppLabSDK.getQuota()</H3>
        <P>Hämtar kvot-status för inloggad användare. Räknas inte mot kvoten.</P>
        <Box sx={codeBg}>{`const quota = await AppLabSDK.getQuota();
// → {
//     limit:        number,   // månadskvot i tokens
//     used:         number,   // förbrukade tokens denna månad
//     remaining:    number,   // återstående tokens
//     percent_used: number,   // 0–100
//     reset_date:   string,   // "YYYY-MM-DD" – nästa månads första dag
//   }`}</Box>

        <H3>AppLabSDK.listBlocks()</H3>
        <P>Listar tillgängliga block med schema och beskrivning.</P>
        <Box sx={codeBg}>{`const blocks = await AppLabSDK.listBlocks();
// → [{ name, status, description, schema }]`}</Box>

        <H3>AppLabSDK.on(event, handler)</H3>
        <P>Registrerar en lyssnare för SDK-events. Anropas aldrig mot kvoten.</P>
        <Box sx={codeBg}>{`AppLabSDK.on('quota_warning', (data) => {
  // Triggas automatiskt när du passerar 80% av månadskvoten
  console.warn(\`Kvot nästan slut: \${data.percent_used}%\`);
});

AppLabSDK.off('quota_warning'); // ta bort lyssnaren`}</Box>

        <H3>Felhantering</H3>
        <P>Alla metoder returnerar Promises. Vid fel kastas ett <Inline>Error</Inline> med egenskapen <Inline>err.code</Inline>:</P>
        <Box sx={codeBg}>{`try {
  const res = await AppLabSDK.run('ai.chat', { messages: [...] });
} catch (err) {
  switch (err.code) {
    case 'QUOTA_EXCEEDED':     // månadskvoten är slut
    case 'PER_REQUEST_LIMIT':  // max_tokens är för högt
    case 'INVALID_PARAMS':     // felaktiga parametrar
    case 'BLOCK_DISABLED':     // kill-switch aktiv (admin)
    case 'NOT_IN_IFRAME':      // appen körs utanför LagbasN
    case 'RATE_LIMITED':       // för många anrop (max 10/sek, 60/min)
    case 'TIMEOUT':            // inget svar inom 30 sekunder
    case 'NETWORK_ERROR':      // generellt nätverksfel
  }
  // err.message är på svenska och lämplig att visa för användaren
}`}</Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 3. Block-katalog ──────────────────────────────────────────────── */}
        <H2 id="block-katalog">3. Block-katalog</H2>
        <P>Block är AppLABs API-primitiver. Fler block tillkommer i framtida faser.</P>

        {BLOCKS.map(b => (
          <Box key={b.name} sx={{ bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 2.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography fontWeight={800} sx={{ color: 'white', fontFamily: 'monospace' }}>{b.name}</Typography>
              <Chip label={b.status === 'available' ? 'Tillgänglig' : 'Kommer snart'}
                size="small"
                sx={{
                  bgcolor: b.status === 'available' ? APPLAB_ACCENT + '22' : 'rgba(255,255,255,0.06)',
                  color:   b.status === 'available' ? APPLAB_ACCENT       : '#666',
                  fontSize: '0.65rem', height: 20,
                }} />
            </Box>
            <Typography fontSize="0.84rem" sx={{ color: '#999', mb: 1.5 }}>{b.description}</Typography>
            <H3>Parametrar</H3>
            <Box sx={codeBg}>{b.schema}</Box>
            <H3>Returnerar</H3>
            <Box sx={codeBg}>{b.returns}</Box>
            <H3>Exempel</H3>
            <Box sx={codeBg}>{b.example}</Box>
          </Box>
        ))}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 4. Modellguide ────────────────────────────────────────────────── */}
        <H2 id="modellguide">4. Modellguide</H2>
        <P>Välj modell baserat på uppgiftens komplexitet. Sonnet 4.6 är rätt val för de flesta ändamål.</P>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {MODELS.map(m => (
            <Box key={m.id} sx={{ bgcolor: '#1a1a1a', border: `1px solid ${m.badgeColor}33`, borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                <Typography fontWeight={800} sx={{ color: 'white' }}>{m.label}</Typography>
                <Box component="code" sx={{ color: '#555', fontSize: '0.75rem', fontFamily: 'monospace' }}>{m.id}</Box>
                <Chip label={m.badge} size="small"
                  sx={{ bgcolor: m.badgeColor + '22', color: m.badgeColor, fontSize: '0.65rem', height: 20, ml: 'auto' }} />
              </Box>
              <Typography fontSize="0.82rem" sx={{ color: '#888', mb: 0.75 }}>{m.tip}</Typography>
              <Typography fontSize="0.75rem" sx={{ color: '#555' }}>
                ${m.costIn}/M input-tokens · ${m.costOut}/M output-tokens
              </Typography>
              {m.warning && (
                <Alert severity="warning" sx={{ mt: 1, bgcolor: 'rgba(255,152,0,0.08)', color: '#ffa000', fontSize: '0.78rem', border: '1px solid rgba(255,152,0,0.2)', '& .MuiAlert-icon': { color: '#ffa000' } }}>
                  {m.warning}
                </Alert>
              )}
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 5. Kvoter & kostnader ─────────────────────────────────────────── */}
        <H2 id="kvoter">5. Kvoter & kostnader</H2>
        <P>Varje LagbasN-användare har en månadskvot i tokens (standard 1 miljon). Kvoten delas av alla AppLAB-appar du använder.</P>

        <H3>Kolla din kvot i appen</H3>
        <Box sx={codeBg}>{`const quota = await AppLabSDK.getQuota();
console.log(\`\${quota.used} / \${quota.limit} tokens (\${quota.percent_used}%)\`);
console.log('Nollställs: ' + quota.reset_date);`}</Box>

        <H3>Vad händer vid kvot-slut?</H3>
        <P>Anropet rejectar med <Inline>err.code === 'QUOTA_EXCEEDED'</Inline>. Visa ett tydligt meddelande för användaren. Kvoten nollställs automatiskt den första dagen varje månad.</P>

        <H3>Tips för att hålla kostnader nere</H3>
        <Box component="ul" sx={{ pl: 2.5, color: '#888', fontSize: '0.85rem', lineHeight: 2 }}>
          {[
            'Välj Haiku 4.5 för enkla uppgifter (klassificering, korta svar).',
            'Håll system-prompts korta – de räknas som input-tokens på varje anrop.',
            'Begränsa max_tokens till vad appen faktiskt behöver.',
            'Återanvänd resultat med localStorage istället för att fråga igen.',
            'Undvik Opus 4.7 om inte kvaliteten verkligen motiverar det.',
          ].map((tip, i) => <li key={i}>{tip}</li>)}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 6. Exempelappar ───────────────────────────────────────────────── */}
        <H2 id="exempelappar">6. Exempelappar</H2>
        <P>Ladda ner och modifiera – eller ladda upp direkt via <Box component="span" onClick={() => navigate('/applab/upload')} sx={{ color: APPLAB_ACCENT, cursor: 'pointer' }}>Ladda upp app</Box>.</P>

        {EXAMPLES.map(ex => (
          <Box key={ex.file} sx={{
            bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 2, p: 2.5, mb: 2,
            display: 'flex', alignItems: 'flex-start', gap: 2,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800} fontSize="0.9rem" sx={{ color: 'white', mb: 0.5 }}>{ex.label}</Typography>
              <Box component="code" sx={{ fontSize: '0.72rem', color: '#555', fontFamily: 'monospace', display: 'block', mb: 0.75 }}>{ex.file}</Box>
              <Typography fontSize="0.82rem" sx={{ color: '#888' }}>{ex.desc}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                component="a"
                href={`https://applab.lagbasn.se/applab-examples/${ex.file}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#666', fontSize: '0.75rem', textTransform: 'none', border: '1px solid rgba(255,255,255,0.1)', px: 1.5 }}
              >
                Förhandsgranska
              </Button>
              <Button
                size="small"
                startIcon={downloading === ex.file
                  ? null
                  : <DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={() => handleDownload(ex.file)}
                disabled={downloading === ex.file}
                sx={{ bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT, fontSize: '0.75rem', textTransform: 'none', border: `1px solid ${APPLAB_ACCENT}44`, px: 1.5 }}
              >
                {downloading === ex.file ? 'Laddar…' : 'Ladda ner'}
              </Button>
            </Box>
          </Box>
        ))}

        <H3>Kom igång med en exempelapp</H3>
        <Box sx={codeBg}>{`1. Ladda ner chat.html
2. Öppna /applab/upload i LagbasN
3. Dra in filen i dropzonen
4. Ge appen ett namn och ladda upp
5. Öppna appen från AppLAB – du har nu en fungerande AI-chatt`}</Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 7. Vanliga frågor ─────────────────────────────────────────────── */}
        <H2 id="faq">7. Vanliga frågor</H2>
        {FAQ.map((f, i) => (
          <Box key={i} sx={{ mb: 2.5 }}>
            <Typography fontWeight={700} fontSize="0.88rem" sx={{ color: '#ddd', mb: 0.5 }}>
              {f.q}
            </Typography>
            <Typography fontSize="0.84rem" sx={{ color: '#888', lineHeight: 1.7 }}>{f.a}</Typography>
          </Box>
        ))}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

        {/* ── 8. Feedback ───────────────────────────────────────────────────── */}
        <H2 id="feedback">8. Saknar du något?</H2>
        <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 3, textAlign: 'center' }}>
          <Typography fontSize="0.9rem" sx={{ color: '#ccc', mb: 1.5 }}>
            Saknar du ett block, en funktion eller ett exempel?
          </Typography>
          <Button
            component="a"
            href="mailto:stribeck@gmail.com?subject=AppLAB – Feedback"
            variant="contained"
            sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, textTransform: 'none', fontWeight: 700 }}
          >
            Skicka feedback →
          </Button>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography fontSize="0.72rem" sx={{ color: '#333' }}>
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span> SDK v2 · LagbasN intern plattform
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
